import { useState, useCallback, useEffect } from "react";
import { apiService } from "@/core/api";
import { API_ENDPOINTS } from "@/core/constants/endpoints";
import { useToast } from "@/core/hooks/useToast";
import {
  OrganizationSearchResult,
  OrganizationSearchViewModel,
} from "../model/types";
import {
  fetchIdeaDetailRequested,
  fetchIdeaInvitationsRequestsRequested,
} from "../store";
import { showToast } from "@/core/store/app";

interface InvitePartnerResponse {
  results: { message: string }[];
}

export const useOrganizationSearchViewModel = (
  ideaId: string | undefined,
  onSuccess?: () => void,
): OrganizationSearchViewModel => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<
    OrganizationSearchResult[]
  >([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] =
    useState<OrganizationSearchResult | null>(null);
  const [joinLoading, setJoinLoading] = useState(false);
  const { showError } = useToast();

  const searchOrganizations = useCallback(
    async (query: string) => {
      if (query.length < 3) {
        setSearchResults([]);
        return;
      }

      setSearchLoading(true);
      try {
        const response = await apiService.get<{
          data: OrganizationSearchResult[];
        }>(API_ENDPOINTS.PARTNERS.ORG_SEARCH(query));
        setSearchResults(response.data);
      } catch (error: any) {
        console.error("Search failed:", error);
        showError(error?.error || "Search failed");
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    },
    [showError],
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchOrganizations(searchQuery);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchOrganizations]);

  const inviteOrganization = useCallback(
    async (orgId: string): Promise<void> => {
      if (!ideaId) return;

      setJoinLoading(true);
      try {
        const data = await apiService.post<InvitePartnerResponse>(
          API_ENDPOINTS.IDEAS.IDEA_INVITE_PARTNET,
          {
            idea_id: parseInt(ideaId),
            partner_id: orgId,
          },
        );

        fetchIdeaDetailRequested(ideaId);
        fetchIdeaInvitationsRequestsRequested();
        // console.log(data, "data");
        showToast({
          type: "success",
          text: data?.results?.[0].message,
        });
        onSuccess?.();
      } catch (error: any) {
        console.error("Failed to invite organization:", error);
        showError(
          error?.error ||
            "User must be organization member to perform this action",
        );
      } finally {
        setJoinLoading(false);
      }
    },
    [ideaId, onSuccess, showError],
  );

  const resetSearch = useCallback(() => {
    setSearchQuery("");
    setSearchResults([]);
    setSelectedOrg(null);
    setSearchLoading(false);
    setJoinLoading(false);
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    searchResults,
    searchLoading,
    selectedOrg,
    setSelectedOrg,
    joinLoading,
    inviteOrganization,
    resetSearch,
  };
};
